import { useEffect, useRef } from 'react';
import { AddLayerCommand, type HistoryManager } from '../core/commands';
import {
  createRasterLayer,
  findLayer,
  validateDimensions,
  type EditorDocument,
} from '../core/model';
import { ReplaceRasterCommand } from '../core/template';
import { RasterTileStore } from '../core/tiles';
import type { DocumentSession } from '../core/session';
import type { AddonContext, AddonPanel, MaipoAddon } from '../addons/types';
import {
  createCloudGeneration,
  createRasterLabJob,
  downloadRasterLabAsset,
  downloadCloudGenerationAsset,
  getCloudAuthState,
  getCloudGeneration,
  getRasterLabJob,
  loadCloudAccount,
  loadRasterLabCapabilities,
  signInToCloud,
  uploadRasterLabAsset,
} from '../../cloud/client';

interface AddonContextLive {
  session: DocumentSession;
  document: EditorDocument;
  history: HistoryManager;
  onNotify: (message: string) => void;
}

let addonTransactions = 0;

/**
 * The history an add-on sees: the document's own HistoryManager, except that
 * `execute` and `commitLive` run inside `withOrigin({ kind: 'addon', id })`,
 * so raw commits are attributed to the add-on in History and the ledger
 * (ADR-009 phase 2). Only synchronous commits are covered, exactly as
 * withOrigin documents, so a user edit made while the add-on awaits is never
 * misattributed. Every other member is the real manager's.
 */
function attributedHistory(history: HistoryManager, addonId: string): HistoryManager {
  const origin = { kind: 'addon', id: addonId } as const;
  return new Proxy(history, {
    get(target, key) {
      if (key === 'execute') return (document: EditorDocument, command: Parameters<HistoryManager['execute']>[1]) => target.withOrigin(origin, () => target.execute(document, command));
      if (key === 'commitLive') return (document: EditorDocument) => target.withOrigin(origin, () => target.commitLive(document));
      const value: unknown = Reflect.get(target, key, target);
      return typeof value === 'function' ? (value as (...args: unknown[]) => unknown).bind(target) : value;
    },
  });
}

/** Builds the same capability-limited context for dock panels and commands. */
export function createAddonContext(addon: MaipoAddon, current: () => AddonContextLive): AddonContext {
  const store = (pixels: Uint8ClampedArray, width: number, height: number) => {
    validateDimensions(width, height);
    if (pixels.length !== width * height * 4) throw new Error('Addon raster buffer length does not match its dimensions.');
    return RasterTileStore.fromImageData(new ImageData(new Uint8ClampedArray(pixels), width, height));
  };

  const histories = new WeakMap<HistoryManager, HistoryManager>();
  const historyFor = (history: HistoryManager) => {
    let wrapped = histories.get(history);
    if (!wrapped) {
      wrapped = attributedHistory(history, addon.id);
      histories.set(history, wrapped);
    }
    return wrapped;
  };

  return {
    get document() { return current().document; },
    get history() { return historyFor(current().history); },
    get activeLayers() {
      const editorDocument = current().document;
      return editorDocument.activeLayerIds.flatMap((id) => {
        const layer = findLayer(editorDocument.root, id);
        return layer ? [layer] : [];
      });
    },
    addRasterLayer(name, pixels, width, height) {
      const layer = createRasterLayer(name.slice(0, 100) || 'Addon layer', width, height);
      layer.raster = store(pixels, width, height);
      const live = current();
      live.history.withOrigin({ kind: 'addon', id: addon.id }, () => live.history.execute(live.document, new AddLayerCommand(layer)));
    },
    replaceActiveRaster(pixels, width, height) {
      const live = current();
      const target = live.document.activeLayerIds
        .map((id) => findLayer(live.document.root, id))
        .find((layer) => layer?.kind === 'raster');
      if (!target) return false;
      const result = live.history.withOrigin({ kind: 'addon', id: addon.id }, () => live.history.execute(live.document, new ReplaceRasterCommand(target.id, store(pixels, width, height), 'Addon result')));
      return result.outcome === 'changed';
    },
    async dispatch(request) {
      const { session } = current();
      // Loaded on first use so the editor's initial bundle does not carry the validators.
      const { semanticRuntime } = await import('../semantic/dispatcher');
      addonTransactions += 1;
      const envelope = {
        txId: `addon-${Date.now().toString(36)}-${String(addonTransactions)}`,
        baseRevision: session.currentRevision,
        ...(request && typeof request === 'object' ? request : {}),
        document: 'local',
      };
      return semanticRuntime(session).dispatch(envelope, { kind: 'addon', id: addon.id });
    },
    notify(message) { current().onNotify(String(message).slice(0, 300)); },
    invalidate() { /* the shell re-renders on history events already */ },
    ...(addon.capabilities?.maipoCloud?.rasterGeneration ? {
      cloud: {
        authState: getCloudAuthState,
        signIn: signInToCloud,
        loadAccount: loadCloudAccount,
        generateRaster: createCloudGeneration,
        getRasterGeneration: getCloudGeneration,
        downloadRasterGeneration: downloadCloudGenerationAsset,
        ...(addon.capabilities?.maipoCloud?.rasterLab ? {
          rasterLab: {
            loadCapabilities: loadRasterLabCapabilities,
            uploadAsset: uploadRasterLabAsset,
            createJob: createRasterLabJob,
            getJob: getRasterLabJob,
            downloadAsset: downloadRasterLabAsset,
          },
        } : {}),
      },
    } : {}),
  };
}

/**
 * Mounts one addon panel into the dock.
 *
 * The addon renders into a plain element and only ever reaches the document
 * through the command system, so its edits are undoable and indistinguishable
 * from the app's own. It never receives React, the renderer, or tile internals.
 */
export function AddonPanelHost({ addon, panel, session, document: editorDocument, history, onNotify }: {
  addon: MaipoAddon;
  panel: AddonPanel;
  session: DocumentSession;
  document: EditorDocument;
  history: HistoryManager;
  onNotify: (message: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Kept in a ref so the panel is mounted once and still sees fresh state; a
  // remount on every document revision would destroy the addon's own UI state.
  const live = useRef({ session, document: editorDocument, history, onNotify });
  live.current = { session, document: editorDocument, history, onNotify };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const context = createAddonContext(addon, () => live.current);

    let dispose: (() => void) | undefined;
    try {
      dispose = panel.mount(container, context);
    } catch (reason) {
      container.textContent = `This panel failed to load: ${reason instanceof Error ? reason.message : String(reason)}`;
    }
    return () => {
      try { dispose?.(); } catch { /* a failing disposer must not break unmount */ }
      container.replaceChildren();
    };
  }, [addon, panel]);

  return <div className="addon-panel" ref={containerRef} />;
}
